import { ModelJson } from './database-model';
import { ContainerType } from './media';

/** @deprecated: Legacy world media - delete when all links has been removed */
interface WorldMediaLink {
    worldKey: string;
}

interface MediaContainerLink {
    type: ContainerType;
    containerKey: string;
}

export type MediaLinkJson = string | WorldMediaLink | MediaContainerLink;

/**
 * A MediaLink representing a link between multiple media containers, that contains the same media.
 * When the media is changed in one container, the changes will automatically be saved in all linked containers as well.
 * This is mainly use for mote translations in order to avoid storing redundant images and to avoid downloading
 * the same image for each language translation when user changes language.
 */
export class MediaLink {
    type: ContainerType;
    containerKey: string;

    constructor(json, containerKey?: string) {
        this.fromJson(json, containerKey);
    }

    public toJson(): ModelJson {
        return {
            type: this.type,
            containerKey: this.containerKey,
        };
    }

    fromJson(json: MediaLinkJson, containerKey?: string): void {
        // If the link was already converted, just user the values from json
        if(this.isContainerLink(json)) {
            this.type = json.type;
            this.containerKey = json.containerKey;
        }
        // Convert world links to new container link format
        else if(this.isDeprecatedWorldLink(json)) {
            this.type = ContainerType.WORLD;
            this.containerKey = json.worldKey;
        }
        // Convert mote media links to new media container format
        else {
            this.type = ContainerType.MOTE;
            this.containerKey = containerKey;
        }
    }

    /**
     * Transform a media link json map to a MediaContainerLinkMap with instances of MediaLink
     */
    public static getMediaContainerLinkMap(json: { [containerKey: string]: MediaLinkJson; }, worldMediaKey: string = ''): MediaContainerLinkMap {
        if(!json) return {};

        // TODO: Delete this when all world links have been converted
        if(json.worldKey) {
            const worldLinks = {};
            worldLinks[worldMediaKey] = new MediaLink(json);
            return worldLinks;
        }

        // Convert entries of json map to Model instances of MediaContainerLinkMap
        const containerMapList = Object.entries(json).map(([containerKey, linkJson]) => {
            // Return entry with media key as map hash
            return [containerKey, new MediaLink(linkJson, containerKey)];
        });
        return Object.fromEntries(containerMapList);
    }

    private isContainerLink(json: any): json is MediaContainerLink {
        return (json?.type && json?.containerKey);
    }

    private isDeprecatedWorldLink(json: any): json is WorldMediaLink {
        return (json?.worldKey);
    }
};

// Map of media link lists of several media objects (raw JSON)
export type MediaLinkMapJson = { [mediaKey: string]: { [containerKey: string]: MediaLinkJson; }; };
// Map of media link lists of several media objects (model instance map)
export type MediaLinkMap = { [mediaKey: string]: MediaContainerLinkMap; };
// Map with mediaContainer key (mote, world etc.) pointing at media mediaKey
export type MediaContainerLinkMap = { [containerKey: string]: MediaLink; };
